/**
 * TileCell — connects a Tile config to the CacheClient and renders TileCard.
 *
 * This is the composition boundary: TileCard is a pure render component (takes
 * a GlanceViewModel), TileCell is the smart component that fetches data and
 * provides the view-model. Keeping them separate makes TileCard trivially
 * testable without a mock cache.
 *
 * In editMode, renders a drag-handle affordance (top-left) per core-flows §3.
 * The drag handle is now DECORATIVE in edit mode — whole tile is drag surface (§3.1).
 *
 * Long-press (500ms) on any tile (non-edit mode) enters edit mode (§2.1, D-016).
 * Jiggle animation applied in edit mode with per-tile phase desync (§2.2).
 * Minus badge (top-left) replaces ✕ button (§2.5, D-016 SHOULD #13).
 *
 * Immutability: functional component; no mutation of props or state.
 */
import { useEffect, useCallback, useState, useRef } from 'react';
import type { Tile, TileSize } from 'briefpane/schema';
import { getTileTypeDef } from 'briefpane/schema';
import type { CacheClient, GlanceViewModel, TileRegistry } from 'briefpane/contracts';
import { useTileViewModel } from '../board/use-tile-view-model.js';
import { useEditModeLongPress } from '../edit-mode/use-edit-mode-long-press.js';
import { TileCard } from './TileCard.js';
import { SizeControl } from './SizeControl.js';

interface TileCellProps {
  readonly tile: Tile;
  readonly cache: CacheClient;
  readonly registry: TileRegistry;
  readonly staggerIndex: number;
  readonly editMode?: boolean;
  /** Called when the drag handle is activated via pointer or keyboard. */
  readonly onDragStart?: (tileId: string, e: React.PointerEvent | React.KeyboardEvent) => void;
  /** Called when the user confirms tile removal. */
  readonly onRemove?: () => void;
  /** Called when the user selects a new size from the resize control in edit mode. */
  readonly onResize?: (size: TileSize) => void;
  /**
   * Called when the SE-corner resize handle receives a pointer-down.
   */
  readonly onResizeStart?: (
    tileId: string,
    currentSize: TileSize,
    allowedSizes: readonly TileSize[],
    e: React.PointerEvent,
  ) => void;
  /**
   * Optional shared ref registry: TileCell writes its latest GlanceViewModel into
   * this map (keyed by tileId) after each render.
   */
  readonly vmRegistry?: React.MutableRefObject<Map<string, { vm: GlanceViewModel; data?: Record<string, unknown> }>>;
  /** Whether any tile is currently being dragged (for jiggle pause + vacancy). */
  readonly isDragging?: boolean;
  /** The tileId being dragged (to pause jiggle + show vacancy on this tile). */
  readonly dragTileId?: string | null;
  /** Called to enter edit mode (for long-press gesture). */
  readonly onEditModeEnter?: () => void;
}

/**
 * Returns true if prefers-reduced-motion is active.
 * Pure function — safe to call on every render.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Derive the attribution object for tiles that legally require it (D-010).
 */
function getTileAttribution(
  tileType: string,
): { readonly text: string; readonly url: string } | undefined {
  if (tileType === 'sports.score') {
    return { text: 'TheSportsDB', url: 'https://www.thesportsdb.com' };
  }
  return undefined;
}

export function TileCell({
  tile,
  cache,
  registry,
  staggerIndex,
  editMode = false,
  onDragStart,
  onRemove,
  onResize,
  onResizeStart,
  vmRegistry,
  isDragging = false,
  dragTileId = null,
  onEditModeEnter,
}: TileCellProps): React.ReactElement {
  const { vm, data, needsConnect } = useTileViewModel({ tile, cache, registry });
  const allowedSizes = getTileTypeDef(tile.type).allowedSizes;
  const attribution = getTileAttribution(tile.type);

  // Minus badge remove state (§2.5).
  const [removePending, setRemovePending] = useState(false);
  const removePendingTimerRef = useRef<number>(-1);

  const openRemovePopover = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (removePendingTimerRef.current !== -1) clearTimeout(removePendingTimerRef.current);
    setRemovePending(true);
    // Auto-dismiss after 4s (§2.5).
    removePendingTimerRef.current = window.setTimeout(() => {
      removePendingTimerRef.current = -1;
      setRemovePending(false);
    }, 4000);
  }, []);

  const closeRemovePopover = useCallback(() => {
    if (removePendingTimerRef.current !== -1) clearTimeout(removePendingTimerRef.current);
    removePendingTimerRef.current = -1;
    setRemovePending(false);
  }, []);

  const confirmRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeRemovePopover();
    onRemove?.();
  }, [onRemove, closeRemovePopover]);

  // Cleanup timer on unmount.
  useEffect(() => {
    return () => {
      if (removePendingTimerRef.current !== -1) clearTimeout(removePendingTimerRef.current);
    };
  }, []);

  // Long-press hook (§2.1): enters edit mode on 500ms hold in non-edit mode.
  const longPress = useEditModeLongPress(
    editMode,
    onEditModeEnter ?? (() => { /* no-op if not wired */ }),
  );

  // Interactive connect handler.
  const handleConnect = useCallback(() => {
    void (async () => {
      try {
        const { connectInteractive } = await import('../../adapters/calendar/google-client.js');
        await connectInteractive();
        window.dispatchEvent(new CustomEvent('briefpane:calendar-connect'));
      } catch {
        // Connection failed — the hook will show error on next refresh
      }
    })();
  }, []);

  // Write the latest { vm, data } into the shared registry.
  useEffect(() => {
    if (vmRegistry) {
      vmRegistry.current.set(tile.id, { vm, ...(data !== null ? { data: data as Record<string, unknown> } : {}) });
    }
  }, [vm, data, tile.id, vmRegistry]);
  useEffect(() => {
    if (!vmRegistry) return undefined;
    const reg = vmRegistry;
    const id = tile.id;
    return () => {
      reg.current.delete(id);
    };
  }, [tile.id, vmRegistry]);

  // Compute jiggle + drag state for this specific tile.
  const isThisTileDragged = isDragging && dragTileId === tile.id;
  const phaseOffset = (staggerIndex * 97) % 700;

  // Wrapper class names.
  const wrapperClasses = [
    editMode ? 'tile-drag-surface' : undefined,
    editMode ? 'tile-jiggle' : undefined,
    (editMode && isThisTileDragged) ? 'tile-jiggle--paused' : undefined,
    isThisTileDragged ? 'tile-lifted' : undefined,
  ].filter(Boolean).join(' ') || undefined;

  // Pointer handlers for the outer wrapper.
  // In edit mode: onDragStart fires on pointerdown (whole-tile drag surface).
  // In non-edit mode: long-press handlers fire on pointerdown.
  const wrapperPointerHandlers = editMode
    ? {
        onPointerDown: onDragStart ? (e: React.PointerEvent) => onDragStart(tile.id, e) : undefined,
      }
    : {
        onPointerDown: longPress.onPointerDown,
        onPointerMove: longPress.onPointerMove,
        onPointerUp: longPress.onPointerUp,
        onPointerCancel: longPress.onPointerCancel,
      };

  const tileLabel = tile.label ?? tile.type;

  return (
    <div
      className={wrapperClasses}
      style={{
        position: 'relative',
        height: '100%',
        // Phase offset for jiggle desync (§2.2).
        '--tile-phase-offset': editMode ? `${phaseOffset}ms` : undefined,
        // Whole-tile drag surface: no text select, no default touch scroll.
        touchAction: editMode ? 'none' : undefined,
        userSelect: editMode ? 'none' : undefined,
        WebkitUserSelect: editMode ? 'none' : undefined,
        // Lift scale + shadow when this tile is being dragged (§3.2 / FIX M2).
        // Gated by reduced-motion: no CSS transforms for users who opted out.
        transform: (isThisTileDragged && !prefersReducedMotion()) ? 'scale(1.06)' : undefined,
        boxShadow: (isThisTileDragged && !prefersReducedMotion()) ? 'var(--shadow-tile-drag)' : undefined,
      } as React.CSSProperties}
      {...wrapperPointerHandlers}
    >
      {/* Minus badge remove affordance (§2.5 / SHOULD #13) */}
      {editMode && onRemove && (
        <div
          style={{
            position: 'absolute',
            top: -8,
            left: -8,
            zIndex: 20,
          }}
        >
          {/* 44px hit target wrapper around 20px badge */}
          <button
            type="button"
            aria-label={`Remove ${tileLabel}`}
            onClick={openRemovePopover}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <span
              className="minus-badge--entering"
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: 'var(--color-negative-accent)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
              }}
              aria-hidden="true"
            >
              {'−'}
            </span>
          </button>

          {/* Confirm popover (§2.5) */}
          {removePending && (
            <div
              role="dialog"
              aria-label={`Confirm remove ${tileLabel}`}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); closeRemovePopover(); } }}
              style={{
                position: 'absolute',
                top: 44,
                left: 0,
                width: 160,
                background: 'var(--color-bg-tile)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                boxShadow: 'var(--shadow-drawer)',
                padding: 'var(--space-3) var(--space-4)',
                zIndex: 30,
              }}
            >
              <p style={{
                fontSize: 'var(--text-secondary)',
                fontWeight: 500,
                margin: '0 0 var(--space-3)',
                color: 'var(--color-text-primary)',
              }}>
                Remove {tileLabel}?
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  aria-label={`Confirm remove ${tileLabel}`}
                  onClick={confirmRemove}
                  style={{
                    background: 'none',
                    border: '1px solid var(--color-negative-accent)',
                    borderRadius: 'var(--radius-button)',
                    color: 'var(--color-negative-accent)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 500,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  Remove
                </button>
                <button
                  type="button"
                  aria-label="Keep tile"
                  onClick={(e) => { e.stopPropagation(); closeRemovePopover(); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-dim)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 500,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  Keep
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decorative drag handle (§3.1): remains as visual affordance in edit mode. */}
      {editMode && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            zIndex: 10,
            color: 'var(--color-text-dim)',
            fontSize: '14px',
            lineHeight: 1,
            padding: '4px',
            borderRadius: '4px',
            backgroundColor: 'var(--color-bg-tile)',
            border: '1px solid var(--color-border)',
            pointerEvents: 'none',
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="currentColor"
            aria-hidden="true"
            style={{ display: 'block' }}
          >
            <circle cx="3" cy="2" r="1.2" />
            <circle cx="9" cy="2" r="1.2" />
            <circle cx="3" cy="6" r="1.2" />
            <circle cx="9" cy="6" r="1.2" />
            <circle cx="3" cy="10" r="1.2" />
            <circle cx="9" cy="10" r="1.2" />
          </svg>
        </div>
      )}

      {/* Keyboard drag handle: still needed for a11y (keyboard nav via use-keyboard-nav). */}
      {editMode && (
        <button
          type="button"
          className="tile-drag-handle"
          aria-label={`Move ${tileLabel} tile`}
          tabIndex={0}
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            zIndex: 10,
            opacity: 0,
            width: 1,
            height: 1,
            overflow: 'hidden',
            border: 'none',
            background: 'none',
            padding: 0,
          }}
          onKeyDown={onDragStart ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') onDragStart(tile.id, e);
          } : undefined}
          onPointerDown={onDragStart ? (e) => onDragStart(tile.id, e) : undefined}
        />
      )}

      {/* Size control: shown in edit mode when more than one size is allowed. */}
      {editMode && onResize && allowedSizes.length > 1 && (
        <SizeControl
          currentSize={tile.display.size}
          allowedSizes={allowedSizes}
          onResize={onResize}
          tileLabel={tileLabel}
        />
      )}

      {/* SE-corner drag-to-resize handle.
          WCAG 2.5.5: visual is 20×20px; interactive (hit) area is ≥44×44px via
          transparent padding (12px top + left, the remaining 12px come from the
          outer container already having 6px right/bottom clearance).
          The padding expands toward the tile interior — away from the minus badge
          (top-left) and the popover (below badge). */}
      {editMode && onResizeStart && allowedSizes.length > 1 && (
        <button
          type="button"
          className="tile-resize-handle"
          aria-label={`Resize ${tileLabel} tile`}
          style={{
            position: 'absolute',
            bottom: 20,
            right: 0,
            zIndex: 11,
            /* 12px padding on each side: 12 + 20 + 12 = 44px hit area */
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            userSelect: 'none',
            touchAction: 'none',
          }}
          onPointerDown={(e) => { e.stopPropagation(); onResizeStart(tile.id, tile.display.size, allowedSizes, e); }}
        >
          {/* 20×20px visual icon — unchanged */}
          <span
            aria-hidden="true"
            style={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-dim)',
              backgroundColor: 'var(--color-bg-tile)',
              border: '1px solid var(--color-border)',
              borderRadius: '4px',
              flexShrink: 0,
            }}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ display: 'block' }}
            >
              <path d="M2 8 L8 8 L8 2" />
              <path d="M5 8 L8 8 L8 5" />
            </svg>
          </span>
        </button>
      )}

      {/* TileCard: vacancy state when this tile is being dragged. */}
      <div
        className={isThisTileDragged ? 'tile-vacancy' : undefined}
        style={{ height: '100%' }}
      >
        <TileCard
          viewModel={vm}
          size={tile.display.size}
          staggerIndex={staggerIndex}
          editMode={false}
          {...(attribution ? { attribution } : {})}
        />
      </div>

      {/* Connect overlay: shown for clientDirect tiles in disconnected state. */}
      {needsConnect && !editMode && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderRadius: 'var(--radius-tile)',
            zIndex: 5,
          }}
        >
          <button
            type="button"
            aria-label="Connect Google Calendar"
            onClick={(e) => { e.stopPropagation(); handleConnect(); }}
            style={{
              background: 'var(--color-accent)',
              border: 'none',
              borderRadius: 'var(--radius-button)',
              color: '#fff',
              fontSize: 'var(--text-label)',
              fontWeight: 600,
              padding: '6px 14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Connect
          </button>
        </div>
      )}
    </div>
  );
}
